import { useCallback, useEffect, useState } from "react";
import { document } from "@packages/global";

export function useModal<T = unknown>(name: string, initialData?: T) {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [data, setData] = useState<T | undefined>(initialData);

  const dispatchModalEvent = useCallback(
    (detail: { isOpen: boolean; data?: T }) => {
      document.dispatchEvent(
        new CustomEvent(`@modal:event:${name}`, {
          detail,
        })
      );
    },
    [name]
  );

  const openModal = useCallback(
    (modalData?: T) => {
      dispatchModalEvent({
        isOpen: true,
        data: modalData,
      });
    },
    [dispatchModalEvent]
  );

  const closeModal = useCallback(
    (modalData?: T) => {
      dispatchModalEvent({
        isOpen: false,
        data: modalData,
      });
    },
    [dispatchModalEvent]
  );

  useEffect(() => {
    const handleModalEvent = (e: Event) => {
      const customEvent = e as CustomEvent<{ isOpen: boolean; data?: T }>;
      setData(customEvent.detail?.data || undefined);
      setIsOpen(customEvent.detail.isOpen);
    };

    document.addEventListener(`@modal:event:${name}`, handleModalEvent);

    return () =>
      document.removeEventListener(`@modal:event:${name}`, handleModalEvent);
  }, [name]);

  return {
    isOpen,
    data,
    openModal,
    closeModal,
  };
}
